import { useEffect, useState } from 'react'
import {
  Button,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
} from '@arco-design/web-react'
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createVendorApi,
  deleteVendorApi,
  listVendorsApi,
  updateVendorApi,
  type VendorMeta,
} from '@/api/models'
import { Pill } from '@/utils/render'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import { modalWidth } from '@/utils/responsive'

interface Props {
  visible: boolean
  onCancel: () => void
}

interface VendorFormValues {
  id?: number
  name: string
  description?: string
  icon?: string
  status: boolean
}

export default function VendorManagerModal({ visible, onCancel }: Props) {
  const { isMobile } = useBreakpoint()
  const qc = useQueryClient()
  const [formVisible, setFormVisible] = useState(false)
  const [editing, setEditing] = useState<VendorMeta | null>(null)
  const [form] = Form.useForm()

  const listQuery = useQuery({
    queryKey: ['vendors-meta', 'manager'],
    queryFn: async () => {
      const data = await listVendorsApi({ page_size: 1000 })
      return data.items || []
    },
    enabled: visible,
  })

  const saveMut = useMutation({
    mutationFn: async (values: VendorFormValues) => {
      const payload = {
        name: values.name.trim(),
        description: values.description || '',
        icon: values.icon || '',
        status: values.status ? 1 : 0,
      }
      if (editing?.id) {
        return updateVendorApi({ ...payload, id: editing.id })
      }
      return createVendorApi(payload)
    },
    onSuccess: async () => {
      Message.success(editing ? '供应商已更新' : '供应商已创建')
      setFormVisible(false)
      setEditing(null)
      form.resetFields()
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['vendors-meta'] }),
        qc.invalidateQueries({ queryKey: ['models-meta'] }),
      ])
      listQuery.refetch()
    },
    onError: (e: Error) => Message.error(e.message || '保存失败'),
  })

  useEffect(() => {
    if (!formVisible) return
    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        description: editing.description || '',
        icon: editing.icon || '',
        status: editing.status === 1,
      })
    } else {
      form.resetFields()
      form.setFieldsValue({ status: true })
    }
  }, [formVisible, editing, form])

  const openCreate = () => {
    setEditing(null)
    setFormVisible(true)
  }

  const openEdit = (row: VendorMeta) => {
    setEditing(row)
    setFormVisible(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await deleteVendorApi(id)
      Message.success('已删除')
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['vendors-meta'] }),
        qc.invalidateQueries({ queryKey: ['models-meta'] }),
      ])
      listQuery.refetch()
    } catch (e) {
      Message.error(e instanceof Error ? e.message : '删除失败')
    }
  }

  const submitForm = async () => {
    try {
      const values = await form.validate()
      await saveMut.mutateAsync(values as VendorFormValues)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const columns = [
    { title: '名称', dataIndex: 'name', width: 160 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (s: number) =>
        s === 1 ? <Pill variant="success">已启用</Pill> : <Pill variant="neutral">已禁用</Pill>,
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '操作',
      width: 100,
      render: (_: unknown, row: VendorMeta) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<IconEdit />}
            aria-label="编辑供应商"
            onClick={() => openEdit(row)}
          />
          <Popconfirm title="确定删除该供应商？" onOk={() => handleDelete(row.id)}>
            <Button
              type="text"
              size="small"
              status="danger"
              icon={<IconDelete />}
              aria-label="删除供应商"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Modal
        title="供应商管理"
        visible={visible}
        onCancel={onCancel}
        footer={null}
        style={{ width: isMobile ? modalWidth(720) : 720 }}
        unmountOnExit
      >
        <div className="mb-3 flex justify-end">
          <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
            新建供应商
          </Button>
        </div>
        <Table
          rowKey="id"
          loading={listQuery.isLoading}
          columns={columns}
          data={listQuery.data || []}
          pagination={false}
          scroll={{ y: 360 }}
          size="small"
        />
      </Modal>

      <Modal
        title={editing ? `编辑供应商 · ${editing.name}` : '新建供应商'}
        visible={formVisible}
        onCancel={() => {
          setFormVisible(false)
          setEditing(null)
        }}
        onOk={submitForm}
        confirmLoading={saveMut.isPending}
        style={{ width: isMobile ? modalWidth(480) : 480 }}
        unmountOnExit
      >
        <Form form={form} layout="vertical">
          <Form.Item field="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input allowClear />
          </Form.Item>
          <Form.Item field="description" label="描述">
            <Input.TextArea rows={2} allowClear />
          </Form.Item>
          <Form.Item field="icon" label="图标">
            <Input placeholder="如 OpenAI、Claude.Color" allowClear />
          </Form.Item>
          <Form.Item field="status" label="启用" triggerPropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
